import { useMemo, useState, Children, PropsWithChildren, useRef, useEffect } from "react";
import { Button } from "react-migration/components/Button";
import cn from "classnames";

type ExpandableListProps = PropsWithChildren<{
  limit: number;
  showCount?: boolean;
  itemsName: string;
  className?: string;
  bottomFade?: boolean;
  animationDuration?: number;
}>;

export function ExpandableList({
  limit,
  showCount,
  itemsName,
  children,
  className = "gap-4",
  bottomFade = false,
  animationDuration = 0,
}: ExpandableListProps) {
  const [expanded, setExpanded] = useState(false);
  const [initialHeight, setInitialHeight] = useState(0);

  const length = Children.count(children);
  const showExpandButton = length > limit;

  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(showExpandButton ? "0px" : "none");

  useEffect(() => {
    if (showExpandButton && contentRef.current && initialHeight === 0) {
      const childrenElements = Array.from(contentRef.current.children) as HTMLElement[];
      const gap = parseFloat(getComputedStyle(contentRef.current).gap) || 0;
      let height = 0;
      for (let i = 0; i < Math.min(limit, childrenElements.length); i++) {
        height += childrenElements[i].offsetHeight;
        if (i < limit - 1) height += gap;
      }
      setInitialHeight(height);
    }
  }, [showExpandButton, limit, initialHeight]);

  useEffect(() => {
    if (contentRef.current) {
      if (!showExpandButton) {
        setMaxHeight("none");
      } else if (expanded) {
        setMaxHeight(`${contentRef.current.scrollHeight}px`);
      } else if (initialHeight > 0) {
        setMaxHeight(`${initialHeight}px`);
      }
    }
  }, [expanded, initialHeight, showExpandButton]);

  return (
    <div
      className={cn("flex flex-col overflow-hidden py-2", bottomFade && "relative", bottomFade && "pb-10!", className)}
    >
      <div
        className={cn("overflow-hidden transition-all ease-in-out")}
        style={{ maxHeight, transitionDuration: `${animationDuration}ms` }}
      >
        <div ref={contentRef} className={cn("flex flex-col", className)}>
          {children}
        </div>
      </div>
      {showExpandButton && (
        <TruncatedOverlay expanded={expanded} bottomFade={bottomFade}>
          <Button
            variant="tertiary"
            size="md"
            onClick={() => setExpanded((v) => !v)}
            className="w-full h-[36px]"
            tailIcon={expanded ? "lt-arrow-up-line" : "lt-arrow-down-line"}
          >
            {expanded ? `Show fewer ${itemsName}` : `Show all ${showCount ? `${length} ` : ""}${itemsName}`}
          </Button>
        </TruncatedOverlay>
      )}
    </div>
  );
}

function TruncatedOverlay({
  children,
  expanded,
  bottomFade,
}: {
  children: React.ReactNode;
  expanded: boolean;
  bottomFade: boolean;
}) {
  return (
    <div
      className={cn("pt-2", {
        "pt-8": !expanded && bottomFade,
        "absolute bottom-0 top-auto inset-0 bg-linear-to-b to-background-surface from-transparent": bottomFade,
      })}
      style={{
        background: expanded ? "transparent" : "linear-gradient(0deg, white 65%, rgba(255, 255, 255, 0))",
      }}
    >
      {children}
    </div>
  );
}
